import type { HTMLAttributes } from 'react'

import {
	COLORS,
	NumberInputField,
	TextInputField,
} from '@monthio/react-components'
import React, { useCallback, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import { requiredFieldError } from './zodErrorMap'

export interface LabelledFormField {
	name: string
	formErrors?: Record<string, string>
	label: string
}

export type FormField<T, K = unknown> = LabelledFormField &
	Omit<HTMLAttributes<HTMLLabelElement>, 'onChange' | 'placeholder'> & {
		placeholder?: T
		value?: T
		onChange: (v?: T) => void
		children?: never
	} & K

const Label = styled.label`
	position: relative;
	padding-bottom: 12px;
	display: flex;
	width: 100%;
	flex-direction: column;
`

const ErrorMessage = styled.span`
	color: ${COLORS.OTHER_COLORS.ERROR_RED};
	position: absolute;
	bottom: 0;
	right: 0;
	font-size: 12px;
	display: block;
	translate: 0 4px;
`

const LabelText = styled.span<{ $isRequiredError: boolean }>`
	${(p) =>
		p.$isRequiredError &&
		css`
			&::after {
				content: '*';
				margin-left: 2px;
				color: ${COLORS.OTHER_COLORS.ERROR_RED};
			}
		`}
`

export const LabelledField: React.FC<
	LabelledFormField & { touched?: boolean } & HTMLAttributes<HTMLLabelElement>
> = ({ label, name, formErrors, touched, children, ...domAttributes }) => {
	const error = (touched && formErrors?.[name]) || undefined
	const isRequiredError = error === requiredFieldError
	return (
		<Label {...domAttributes}>
			<LabelText $isRequiredError={isRequiredError}>{label}</LabelText>
			{children}
			{error && !isRequiredError && <ErrorMessage>{error}</ErrorMessage>}
		</Label>
	)
}

const asNumber = (s?: string | number): number => s as unknown as number

export const LabelledNumberField: React.FC<
	FormField<number> & { formatValue?: (n: number) => string }
> = ({
	label,
	value,
	onChange,
	name,
	formErrors,
	placeholder,
	formatValue,
}) => {
	const touched = useRef<boolean>()
	const [isFocused, setIsFocused] = useState(false)
	const format = useCallback(
		(value?: number | '') => {
			if (value === undefined || value === '') {
				return value
			}
			return formatValue ? formatValue(value) : value
		},
		[formatValue],
	)

	return (
		<LabelledField
			label={label}
			name={name}
			formErrors={formErrors}
			touched={touched.current}
		>
			<NumberInputField
				InputProps={{
					onFocus: () => setIsFocused(true),
					onBlur: () => setIsFocused(false),
				}}
				variant="outlined"
				placeholder={
					formatValue && placeholder !== undefined
						? formatValue(placeholder)
						: placeholder?.toString()
				}
				value={asNumber(isFocused ? value : format(value))}
				onChange={(x) => {
					const coerced = coerceNumberInput(x.target.value)
					onChange(asNumber(coerced))
					touched.current = true
				}}
			/>
		</LabelledField>
	)
}

function coerceNumberInput(input?: string): number | string | undefined {
	if (!input) {
		return undefined
	}

	const numberValue = Number(input)
	if (isNaN(numberValue)) {
		return input
	}
	return numberValue
}

export const LabelledTextField: React.FC<FormField<string>> = ({
	label,
	value,
	onChange,
	name,
	formErrors,
	placeholder,
}) => {
	const touched = useRef<boolean>()
	return (
		<LabelledField
			label={label}
			name={name}
			formErrors={formErrors}
			touched={touched.current}
		>
			<TextInputField
				variant="outlined"
				placeholder={placeholder}
				value={value || ''}
				onChange={(x) => {
					onChange(x.target.value || undefined)
					touched.current = true
				}}
			/>
		</LabelledField>
	)
}
